import {Component, Injector, ViewChild, ViewEncapsulation, AfterViewInit, OnInit, OnDestroy} from '@angular/core';
import { appModuleAnimation } from '@axilla/axilla-shared';
import { AppComponentBase } from '@shared/common/app-component-base';
import {
    BasketArticleDto,
    OrderGuideSimpleDto, PagedResultDtoOfArticleDto
} from '@shared/service-proxies/service-proxies';
import { Paginator } from 'primeng/paginator';
import { Table } from 'primeng/table';
import {Subject} from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import {OrderState, OrderStateService} from '@app/webshop/field-agent/shared/services/order-state.service';
import {FieldAgentOrderService} from '@app/webshop/field-agent/shared/services/field-agent-order.service';
import {Router} from '@angular/router';
import {NgxSpinnerService} from '@node_modules/ngx-spinner';
import {CombineViewService} from '@app/webshop/shared/services/combine-view.service';
import {WebshopService} from '@app/shared/common/shop-system/services/webshop.service';
import {BasePermissionDefinition} from '@shared/permission/base-permission-definition';
import _ from 'lodash';

@Component({
    templateUrl: './field-agent-promotions.component.html',
    styleUrls: ['./field-agent-promotions.component.less'],
    animations: [appModuleAnimation()]
})
export class FieldAgentPromotionsComponent extends AppComponentBase implements OnInit, OnDestroy {
    protected readonly BasePermissionDefinition = BasePermissionDefinition;

    spinnerService: NgxSpinnerService;

    @ViewChild('dataTable', { static: true }) dataTable: Table;
    @ViewChild('paginator', { static: true }) paginator: Paginator;

    protected readonly destroy$ = new Subject<void>(); // Used to signal unsubscription

    get brandsValue(): OrderGuideSimpleDto[] {
        return this._fieldAgentOrderService.brandsValue;
    }

    constructor(
        injector: Injector,
        private _orderStateService: OrderStateService,
        private _fieldAgentOrderService: FieldAgentOrderService,
        private _combineViewService: CombineViewService,
        private _webshopService: WebshopService,
        private _router: Router
    ) {
        super(injector);

        this.spinnerService = injector.get(NgxSpinnerService);
    }

    ngOnInit(): void {
        if (!this.isGuided) {
            this._router.navigate([`/app/webshop/dashboard`]);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }


    getLocalWarehouse(event?: any) {
        this.spinnerService.show();

        const skip = event?.event?.first ?? 0;
        const take = event?.event?.rows ?? this.rowsAmount;

        const userId = abp.session.impersonatorUserId;

        this._fieldAgentOrderService.getLocalWarehouse$(userId, skip, take)
                .pipe(
                    takeUntil(this.destroy$) // Automatically unsubscribes when destroy$ emits
                )
            .subscribe((result: PagedResultDtoOfArticleDto) => {
                this._combineViewService.setItems(result);
                this.spinnerService.hide();
            });
    }

    beginOrdering() {
        abp.message.confirm(
            'Do you want to continue?'
            , 'Selecting Templates'
            , (result: boolean) => {
                if (result) {
                    this.continue();
                }
            }
            , {
                showCloseButton: true,
                customClass: {
                    container: 'modal-like'
                }
            });
    }

    continue() {
        this._router.navigate(['/app/webshop/field-agent/order-confirmation']);
        this._orderStateService.confirmOrder();
    }

    get isAllowNavigateToOrderConfirmation(): boolean {
        return this._fieldAgentOrderService.isAllowNavigateToOrderConfirmation;
    }

    get isDisabled(): boolean {
        return this.webshopService.basketCounterValue === 0;
    }

    get isAmountFitMinimumAmountRequirements(): boolean {
        return this._webshopService.checkMinimumAmountRequirements();
    }

    verifyPromotions() {
        if (this.isAllowNavigateToOrderConfirmation) {
            this.continue();
        } else {
            this._router.navigate([`/app/webshop/basket-overview`]);
            this._orderStateService.verifyPromotion();
        }
    }

    onQuickAction(input: BasketArticleDto) {
        if (this.permissionChecker.isGranted(BasePermissionDefinition.Webshop.WMS.EditLocalStock)) {

            let article = _.cloneDeep(input) as BasketArticleDto;
            article.quantity = article.quantity * -1;

            const data = {
                article: article,
                isSelectingArticles: true
            };
            abp.event.trigger('app.show.editLocalWarehouseModal', data);
        }
    }

    isPromotionLimitExceeded(): boolean {
        const result = this.totalItemAmount / this.promotionValueInEuro;

        return result < 1.02 && result > 1.00;
    }

    get orderAmount(): number {
        return +this._fieldAgentOrderService.orderTotalAmountValue;
    }

    get promotionValue(): number {
        return this._fieldAgentOrderService.selectedOrderGuideTemplate.reduction;
    }

    get promotionValueInEuro(): number {
        if (this.promotionValue > 0) {
            return this.orderAmount * (this.promotionValue / 100);
        }

        return 0;
    }

    get totalItemAmount(): number {
        return this._webshopService.totalItemAmount;
    }

}
